import { Card, CardBody, CardHeader } from '@heroui/react';
import React, { useState } from 'react';

import { ErrorCard } from '../../../../components/ErrorCard';
import { useWorkspaceDirectory } from '../../hooks';
import type { AssignableLocation } from '../../types';
import { MemberDetailPanel } from '../MemberDetailPanel';
import { MemberTable } from '../MemberTable';
import { MemberWorkplacesPanel } from '../MemberWorkplacesPanel';

export interface DirectoryTabProps {
  workspaceId: string;
  /** The filter choices, read once by the page and shared with the People tab. */
  locations: AssignableLocation[];
}

/**
 * The workspace's whole directory, as the directory read answers it. The People
 * tab asks a narrower question — the members the caller shares a location with
 * — so the two lists are shown side by side rather than merged.
 *
 * Listed by name: the directory answers for anyone in the workspace, so it is
 * not the place to show where each of them works. Picking a member reads that
 * one member and opens it in the panel.
 */
export const DirectoryTab: React.FC<DirectoryTabProps> = ({
  workspaceId,
  locations,
}) => {
  // The directory answers by name, so the search box is the only control.
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState('10');

  // The member whose panel is open; null is closed, and skips the read.
  const [openUserId, setOpenUserId] = useState<string | null>(null);
  const [workplacesUserId, setWorkplacesUserId] = useState<string | null>(null);

  const {
    members,
    isLoading,
    error,
    notFound,
    totalCount,
    pageNumber,
    hasNextPage,
    hasPreviousPage,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    refetch,
  } = useWorkspaceDirectory(workspaceId, { searchTerm }, Number(pageSize));

  if (error) {
    return (
      <ErrorCard
        title="Could not load the directory"
        message={error.message}
        onRetry={refetch}
      />
    );
  }

  if (notFound) {
    return (
      <ErrorCard
        title="Workspace not found"
        message="Check the link, or pick the workspace from the list."
      />
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <p className="text-small text-default-500">
            Every member of the workspace, not only the ones you share a
            location with.
          </p>
        </CardHeader>
        <CardBody className="gap-4">
          <MemberTable
            members={members}
            locations={locations}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            isLoading={isLoading}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            totalCount={totalCount}
            pageNumber={pageNumber}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            onNextPage={goToNextPage}
            onPreviousPage={goToPreviousPage}
            onFirstPage={goToFirstPage}
            showAssignments={false}
            showPlaceAndRoleFilters={false}
            onSelectMember={(member) => setOpenUserId(member.userId)}
            onShowWorkplaces={(member) => setWorkplacesUserId(member.userId)}
          />
        </CardBody>
      </Card>

      <MemberDetailPanel
        workspaceId={workspaceId}
        userId={openUserId}
        onClose={() => setOpenUserId(null)}
      />

      <MemberWorkplacesPanel
        workspaceId={workspaceId}
        userId={workplacesUserId}
        onClose={() => setWorkplacesUserId(null)}
      />
    </>
  );
};
